import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal,
  Steps,
  Tabs,
  Input,
  Button,
  Space,
  Row,
  Col,
  Card,
  Statistic,
  Alert,
  Tooltip,
  Tag,
  Typography,
  Descriptions,
  Spin,
  Divider,
  App,
} from 'antd';
import {
  TranslationOutlined,
  AppleOutlined,
  AndroidOutlined,
  TeamOutlined,
  ReloadOutlined,
  SendOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {
  BROADCAST_TITLE_MAX,
  BROADCAST_BODY_MAX,
  BroadcastEstimateResponse,
  BroadcastContentMap,
  BroadcastFilter,
  BroadcastStatus,
  BroadcastTestSendResponse,
  BroadcastLanguage,
  NotificationStatus,
} from '@mygate/common';
import { broadcastsService } from '@/services/broadcasts';
import { ApiError } from '@/services/api';
import { PushPreview } from './PushPreview';
import { FilterBuilder, FilterSummary, defaultBroadcastFilter } from './FilterBuilder';
import { LangCode, LANG_KEYS, LANGUAGE_LABELS } from './constants';

const { TextArea } = Input;
const { Text } = Typography;

interface ContentDraft {
  title: string;
  body: string;
  /** true once filled by the translate button and not yet hand-edited by staff. */
  auto: boolean;
}

/** LangCode and BroadcastLanguage (SupportedLanguage enum) share the same runtime string values. */
function toBroadcastLanguage(lang: LangCode): BroadcastLanguage {
  return lang as unknown as BroadcastLanguage;
}

/** Structural equality for the small, JSON-safe BroadcastFilter shape — used to detect an unapplied edit. */
function filtersEqual(a: BroadcastFilter | undefined, b: BroadcastFilter | undefined): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function emptyDraft(): ContentDraft {
  return { title: '', body: '', auto: false };
}

function emptyContent(): Record<LangCode, ContentDraft> {
  return { de: emptyDraft(), en: emptyDraft(), ru: emptyDraft(), pl: emptyDraft() };
}

/** Trims content and drops optional languages that aren't fully filled in. */
function buildContentMap(content: Record<LangCode, ContentDraft>): BroadcastContentMap {
  const map: BroadcastContentMap = {
    de: { title: content.de.title.trim(), body: content.de.body.trim() },
  };
  (['en', 'ru', 'pl'] as const).forEach(lang => {
    const title = content[lang].title.trim();
    const body = content[lang].body.trim();
    if (title && body) {
      map[lang] = { title, body };
    }
  });
  return map;
}

interface BroadcastWizardProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful real send — parent should close and refresh the history list. */
  onSent: () => void;
}

export function BroadcastWizard({ open, onClose, onSent }: BroadcastWizardProps) {
  const { message, modal } = App.useApp();

  const [currentStep, setCurrentStep] = useState(0);
  const [activeLangTab, setActiveLangTab] = useState<LangCode>('de');
  const [content, setContent] = useState<Record<LangCode, ContentDraft>>(emptyContent());
  const [translating, setTranslating] = useState(false);

  const [estimate, setEstimate] = useState<BroadcastEstimateResponse | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);

  // Audience filter (MYG-437): `draftFilter` tracks the FilterBuilder's live edits, `appliedFilter` is the
  // filter the last successful estimate was actually computed with. They diverge as soon as the staff member
  // edits a condition after fetching — Next/Send stay disabled until "Apply filters" reconciles them.
  const [draftFilter, setDraftFilter] = useState<BroadcastFilter | undefined>(defaultBroadcastFilter());
  const [appliedFilter, setAppliedFilter] = useState<BroadcastFilter | undefined>(undefined);
  // Bumped every time the wizard is (re)opened so FilterBuilder remounts with a clean draft.
  const [filterBuilderKey, setFilterBuilderKey] = useState(0);
  const audienceAutoFetchedRef = useRef(false);

  const [testPhone, setTestPhone] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [lastTestResult, setLastTestResult] = useState<BroadcastTestSendResponse | null>(null);

  const [sending, setSending] = useState(false);

  // MYG-438: replay token — one per wizard session, so a lost-response retry of the same session
  // returns the original broadcast instead of sending twice
  const idempotencyKeyRef = useRef(crypto.randomUUID());

  // Reset the whole wizard every time it's freshly opened.
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setActiveLangTab('de');
      setContent(emptyContent());
      setTranslating(false);
      setEstimate(null);
      setDraftFilter(defaultBroadcastFilter());
      setAppliedFilter(undefined);
      setFilterBuilderKey(prev => prev + 1);
      audienceAutoFetchedRef.current = false;
      setTestPhone('');
      setLastTestResult(null);
      setSending(false);
      idempotencyKeyRef.current = crypto.randomUUID();
    }
  }, [open]);

  const fetchEstimate = useCallback(
    async (filter: BroadcastFilter | undefined) => {
      setEstimateLoading(true);
      try {
        const response = await broadcastsService.getEstimate(filter);
        if (response.success && response.data) {
          const data = response.data;
          setEstimate(data);
          setAppliedFilter(filter);
          setTestPhone(prev => prev || data.testPhoneNumber || '');
        }
      } catch (error) {
        message.error(error instanceof ApiError ? error.message : 'Failed to load audience estimate');
      } finally {
        setEstimateLoading(false);
      }
    },
    [message],
  );

  // Fetch the estimate exactly once, the first time the audience step is reached (seeded default filter).
  // Later changes go through the explicit "Apply filters" button so estimate + filter stay reconciled.
  useEffect(() => {
    if (open && currentStep === 1 && !audienceAutoFetchedRef.current) {
      audienceAutoFetchedRef.current = true;
      void fetchEstimate(draftFilter);
    }
  }, [open, currentStep, draftFilter, fetchEstimate]);

  const updateField = (lang: LangCode, field: 'title' | 'body', value: string) => {
    setContent(prev => ({ ...prev, [lang]: { ...prev[lang], [field]: value, auto: false } }));
  };

  const isDeValid = content.de.title.trim().length > 0 && content.de.body.trim().length > 0;

  // True once the staff member has edited the filter since the last successful estimate fetch.
  const filterDirty = !estimate || !filtersEqual(draftFilter, appliedFilter);

  const canTranslate = content[activeLangTab].title.trim().length > 0 && content[activeLangTab].body.trim().length > 0;

  const runTranslate = async (targetLanguages: LangCode[]) => {
    const source = content[activeLangTab];

    setTranslating(true);
    try {
      const response = await broadcastsService.translate({
        sourceLanguage: toBroadcastLanguage(activeLangTab),
        title: source.title.trim(),
        body: source.body.trim(),
        targetLanguages: targetLanguages.map(toBroadcastLanguage),
      });
      if (response.success && response.data) {
        const { translations } = response.data;
        setContent(prev => {
          const next = { ...prev };
          (Object.keys(translations) as LangCode[]).forEach(lang => {
            const translated = translations[lang];
            if (translated && targetLanguages.includes(lang)) {
              next[lang] = { title: translated.title, body: translated.body, auto: true };
            }
          });
          return next;
        });
        message.success('Translations filled in — please review before sending');
      }
    } catch (error) {
      if (error instanceof ApiError && error.code === 'TRANSLATION_FAILED') {
        message.error('Automatic translation failed. Please fill in the other languages manually.');
      } else {
        message.error(error instanceof ApiError ? error.message : 'Failed to translate content');
      }
    } finally {
      setTranslating(false);
    }
  };

  const handleTranslate = () => {
    const targetLanguages = LANG_KEYS.filter(lang => lang !== activeLangTab);
    const editedLanguages = targetLanguages.filter(
      lang => content[lang].title.trim().length > 0 && content[lang].body.trim().length > 0 && !content[lang].auto,
    );

    if (editedLanguages.length === 0) {
      void runTranslate(targetLanguages);
      return;
    }

    const nonConflictingLanguages = targetLanguages.filter(lang => !editedLanguages.includes(lang));

    modal.confirm({
      title: 'Overwrite edited translations?',
      content: `Hand-edited content will be overwritten for: ${editedLanguages
        .map(lang => LANGUAGE_LABELS[lang])
        .join(', ')}.`,
      okText: 'Overwrite',
      cancelText: 'Cancel',
      onOk: () => runTranslate(targetLanguages),
      onCancel: () => {
        if (nonConflictingLanguages.length > 0) {
          void runTranslate(nonConflictingLanguages);
        }
      },
    });
  };

  const handleSendTest = async () => {
    if (!testPhone.trim()) return;
    setTestSending(true);
    setLastTestResult(null);
    try {
      const response = await broadcastsService.sendTest({
        phoneNumber: testPhone.trim(),
        content: buildContentMap(content),
      });
      if (response.success && response.data) {
        setLastTestResult(response.data);
        if (response.data.status === NotificationStatus.FAILED) {
          message.error('Test push could not be queued — check push infrastructure');
        } else {
          message.success(`Test push delivered (${LANGUAGE_LABELS[response.data.language as LangCode]} version)`);
        }
      }
    } catch (error) {
      if (error instanceof ApiError && error.code === 'USER_NOT_FOUND') {
        message.error('No app user with this phone number');
      } else if (error instanceof ApiError && error.code === 'DEVICE_NOT_REGISTERED') {
        message.error('This user has no active push devices');
      } else {
        message.error(error instanceof ApiError ? error.message : 'Failed to send test notification');
      }
    } finally {
      setTestSending(false);
    }
  };

  const handleConfirmSend = () => {
    const total = estimate?.total ?? 0;
    modal.confirm({
      title: 'Send broadcast to everyone?',
      content: `This will send a push notification to ${total} user${total === 1 ? '' : 's'} immediately. This cannot be undone.`,
      okText: `Send to ${total} user${total === 1 ? '' : 's'}`,
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: handleSend,
    });
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const response = await broadcastsService.send(buildContentMap(content), appliedFilter, idempotencyKeyRef.current);
      if (response.success && response.data) {
        // A 200 can carry a FAILED broadcast: partial dispatch failure, or an idempotent replay of an
        // earlier failed attempt (which is NOT retried — recovery needs a fresh wizard session)
        if (response.data.broadcast.status === BroadcastStatus.FAILED) {
          message.error('Broadcast finished with errors — open it in the history for details');
        } else {
          message.success('Broadcast is sending');
        }
        onSent();
      }
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const providedLanguages = LANG_KEYS.filter(
    lang => content[lang].title.trim().length > 0 && content[lang].body.trim().length > 0,
  );
  const missingLanguages = LANG_KEYS.filter(lang => lang !== 'de' && !providedLanguages.includes(lang));

  const stepItems = [{ title: 'Compose' }, { title: 'Audience' }, { title: 'Test send' }, { title: 'Confirm & send' }];

  const ComposeStep = (
    <div>
      <Space wrap style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Tooltip title="Uses AI translation — usually takes 10-30 seconds">
          <Button
            icon={<TranslationOutlined />}
            loading={translating}
            disabled={!canTranslate}
            onClick={handleTranslate}
          >
            Translate to other languages
          </Button>
        </Tooltip>
      </Space>
      <Tabs
        activeKey={activeLangTab}
        onChange={key => setActiveLangTab(key as LangCode)}
        items={LANG_KEYS.map(lang => ({
          key: lang,
          label: (
            <span>
              {LANGUAGE_LABELS[lang]}
              {lang === 'de' && (
                <Text type="danger" style={{ marginLeft: 4 }}>
                  *
                </Text>
              )}
              {content[lang].auto && content[lang].title && (
                <Tag color="blue" style={{ marginLeft: 6 }}>
                  Translated
                </Tag>
              )}
            </span>
          ),
          children: (
            <Row gutter={[24, 24]}>
              <Col xs={24} md={14}>
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Title</Text>
                  <Input
                    value={content[lang].title}
                    onChange={e => updateField(lang, 'title', e.target.value)}
                    placeholder="Notification title"
                    maxLength={BROADCAST_TITLE_MAX}
                    showCount
                  />
                </div>
                <div>
                  <Text strong>Message</Text>
                  <TextArea
                    value={content[lang].body}
                    onChange={e => updateField(lang, 'body', e.target.value)}
                    placeholder="Notification message"
                    rows={4}
                    maxLength={BROADCAST_BODY_MAX}
                    showCount
                    style={{ resize: 'none' }}
                  />
                </div>
              </Col>
              <Col xs={24} md={10}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Live preview
                </Text>
                <PushPreview title={content[lang].title} body={content[lang].body} />
              </Col>
            </Row>
          ),
        }))}
      />
      {!isDeValid && (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message="German title and message are required — they're the fallback for every user."
        />
      )}
    </div>
  );

  const AudienceStep = (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Audience filter
      </Text>
      <FilterBuilder
        key={filterBuilderKey}
        initialFilter={draftFilter ?? defaultBroadcastFilter()}
        onChange={setDraftFilter}
      />
      <Space wrap style={{ width: '100%', justifyContent: 'flex-end', margin: '16px 0' }}>
        {filterDirty && <Text type="warning">Filters changed — apply to refresh the estimate before continuing.</Text>}
        <Button
          type={filterDirty ? 'primary' : 'default'}
          icon={<ReloadOutlined />}
          onClick={() => fetchEstimate(draftFilter)}
          loading={estimateLoading}
        >
          {estimate ? 'Apply filters / Refresh' : 'Apply filters'}
        </Button>
      </Space>
      <Divider style={{ margin: '8px 0 16px' }} />
      {estimateLoading && !estimate ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : estimate ? (
        <>
          {estimate.total === 0 && (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              showIcon
              message="No recipients"
              description="No users currently match this broadcast's audience. Sending is disabled."
            />
          )}
          <Row gutter={16}>
            <Col span={8}>
              <Card>
                <Statistic title="Total recipients" value={estimate.total} prefix={<TeamOutlined />} />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic title="iOS devices" value={estimate.byPlatform.ios} prefix={<AppleOutlined />} />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic title="Android devices" value={estimate.byPlatform.android} prefix={<AndroidOutlined />} />
              </Card>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            {(Object.entries(estimate.byLanguage) as Array<[LangCode, number]>)
              .filter(([, count]) => count > 0)
              .map(([lang, count]) => (
                <Col span={6} key={lang}>
                  <Card size="small">
                    <Statistic title={LANGUAGE_LABELS[lang]} value={count} />
                  </Card>
                </Col>
              ))}
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Excluded (marketing opt-out)"
                  value={estimate.excludedByOptOut}
                  valueStyle={{ color: '#8c8c8c' }}
                />
              </Card>
            </Col>
          </Row>
          {/* MYG-821: who agreed to promotional pushes, and whether that decides the audience */}
          <Alert
            style={{ marginTop: 16 }}
            type={estimate.consentRequired ? 'warning' : 'info'}
            showIcon
            message={
              estimate.consentRequired
                ? `Only users with explicit consent are counted: ${estimate.excludedWithoutConsent} left out because they never saved their notification settings.`
                : `${estimate.withExplicitConsent} of ${estimate.total} recipients saved their notification settings with promotional notifications on. Explicit consent is not required (App Settings); with it, only they would get this broadcast.`
            }
          />
        </>
      ) : null}
    </div>
  );

  const TestSendStep = (
    <div>
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Recommended: send yourself a test before broadcasting to everyone"
        description="Test send is optional, but it's the best way to catch typos or formatting issues."
      />
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text strong>Phone number</Text>
          <Input
            value={testPhone}
            onChange={e => setTestPhone(e.target.value)}
            placeholder="+491701234567"
            style={{ marginTop: 4 }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            The test message will be sent only to this account; the number is remembered for next time.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<SendOutlined />}
          loading={testSending}
          disabled={!testPhone.trim() || !isDeValid}
          onClick={handleSendTest}
        >
          Send test
        </Button>
        {lastTestResult &&
          (lastTestResult.status === NotificationStatus.FAILED ? (
            <Alert
              type="error"
              showIcon
              message={`Test push failed to send (${LANGUAGE_LABELS[lastTestResult.language as LangCode]} version)`}
            />
          ) : (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message={`Test push delivered in ${LANGUAGE_LABELS[lastTestResult.language as LangCode]}`}
            />
          ))}
        <Divider style={{ margin: '8px 0' }} />
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Preview (German fallback)
          </Text>
          <PushPreview title={content.de.title} body={content.de.body} />
        </div>
      </Space>
    </div>
  );

  const ConfirmStep = (
    <div>
      {filterDirty && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="Audience filter changed since the last estimate"
          description="Go back to the Audience step and click Apply filters to refresh before sending."
        />
      )}
      <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Recipients">
          {estimate ? `${estimate.total} user${estimate.total === 1 ? '' : 's'}` : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Audience filter">
          <FilterSummary filter={appliedFilter} />
        </Descriptions.Item>
        <Descriptions.Item label="Languages provided">
          <Space wrap>
            {providedLanguages.map(lang => (
              <Tag key={lang} color="blue">
                {LANGUAGE_LABELS[lang]}
              </Tag>
            ))}
          </Space>
          {missingLanguages.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Users with {missingLanguages.map(lang => LANGUAGE_LABELS[lang]).join(', ')} preference receive the
                German version.
              </Text>
            </div>
          )}
        </Descriptions.Item>
      </Descriptions>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        Preview (German)
      </Text>
      <PushPreview title={content.de.title} body={content.de.body} />
    </div>
  );

  const stepContents = [ComposeStep, AudienceStep, TestSendStep, ConfirmStep];

  const canGoNext =
    currentStep === 0 ? isDeValid : currentStep === 1 ? !!estimate && estimate.total > 0 && !filterDirty : true;

  const handleNext = () => {
    if (currentStep === 0 && !isDeValid) {
      message.error('Please fill in the German title and message before continuing');
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, stepItems.length - 1));
  };

  const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 0));

  const footer = (
    <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
      <Button onClick={onClose}>Cancel</Button>
      <Space wrap>
        {currentStep > 0 && <Button onClick={handleBack}>Back</Button>}
        {currentStep < stepItems.length - 1 ? (
          <Button type="primary" onClick={handleNext} disabled={!canGoNext}>
            Next
          </Button>
        ) : (
          <Button
            type="primary"
            danger
            icon={<SendOutlined />}
            loading={sending}
            disabled={!estimate || estimate.total === 0 || filterDirty}
            onClick={handleConfirmSend}
          >
            Review &amp; send
          </Button>
        )}
      </Space>
    </Space>
  );

  return (
    <Modal title="New broadcast" open={open} onCancel={onClose} width={900} footer={footer} maskClosable={false}>
      <Steps current={currentStep} items={stepItems} style={{ marginBottom: 24 }} />
      {stepContents[currentStep]}
    </Modal>
  );
}
